import { useState, useEffect } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Mail,
  ShieldBan,
  Send,
  AlertCircle,
  Info,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const EmailSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [warning, setWarning] = useState(null);

  // Blocked emails state
  const [newPattern, setNewPattern] = useState('');
  const [newReason, setNewReason] = useState('');
  const [addingBlocked, setAddingBlocked] = useState(false);
  const [removingPattern, setRemovingPattern] = useState(null);

  // Forwarding state
  const [forwardingList, setForwardingList] = useState([]);
  const [newForwardEmail, setNewForwardEmail] = useState('');
  const [savingForwarding, setSavingForwarding] = useState(false);
  const [forwardingChanged, setForwardingChanged] = useState(false);

  const showMessage = (setter, message) => {
    setter(message);
    setTimeout(() => setter(null), 5000);
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setForwardingList(data.forward_to || []);
        setForwardingChanged(false);
      } else {
        setError('Failed to load email settings');
      }
    } catch {
      setError('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleAddBlocked = async (e) => {
    e.preventDefault();
    if (!newPattern.trim()) return;

    setAddingBlocked(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings/blocked`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pattern: newPattern.trim(), reason: newReason.trim() || null }),
      });
      const data = await response.json();
      if (response.ok) {
        setSettings(data.settings);
        setNewPattern('');
        setNewReason('');
        showMessage(setSuccess, 'Blocked pattern added');
        if (data.warning) showMessage(setWarning, data.warning);
      } else {
        showMessage(setError, data.detail || 'Failed to add blocked pattern');
      }
    } catch {
      showMessage(setError, 'Failed to add blocked pattern');
    } finally {
      setAddingBlocked(false);
    }
  };

  const handleRemoveBlocked = async (pattern) => {
    setRemovingPattern(pattern);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings/blocked`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pattern }),
      });
      const data = await response.json();
      if (response.ok) {
        setSettings(data.settings);
        showMessage(setSuccess, 'Blocked pattern removed');
        if (data.warning) showMessage(setWarning, data.warning);
      } else {
        showMessage(setError, data.detail || 'Failed to remove pattern');
      }
    } catch {
      showMessage(setError, 'Failed to remove pattern');
    } finally {
      setRemovingPattern(null);
    }
  };

  const handleAddForwardEmail = () => {
    const email = newForwardEmail.trim().toLowerCase();
    if (!email || forwardingList.includes(email)) return;
    setForwardingList([...forwardingList, email]);
    setNewForwardEmail('');
    setForwardingChanged(true);
  };

  const handleRemoveForwardEmail = (emailToRemove) => {
    setForwardingList(forwardingList.filter((e) => e !== emailToRemove));
    setForwardingChanged(true);
  };

  const handleSaveForwarding = async () => {
    setSavingForwarding(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings/forwarding`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ forward_to: forwardingList }),
      });
      const data = await response.json();
      if (response.ok) {
        setSettings(data.settings);
        setForwardingList(data.settings.forward_to || []);
        setForwardingChanged(false);
        showMessage(setSuccess, 'Forwarding addresses saved');
        if (data.warning) showMessage(setWarning, data.warning);
      } else {
        showMessage(setError, data.detail || 'Failed to save forwarding addresses');
      }
    } catch {
      showMessage(setError, 'Failed to save forwarding addresses');
    } finally {
      setSavingForwarding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const blockedEmails = settings?.blocked_emails || [];

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
          <Mail className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      )}
      {warning && (
        <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {warning}
        </div>
      )}

      {/* Blocked Emails Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldBan className="h-5 w-5 text-red-500" />
            Blocked Emails
          </CardTitle>
          <CardDescription>
            Block specific email addresses or entire domains from the contact form and email forwarding.
            Use <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">@domain.com</code> to block all emails from a domain.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add form */}
          <form onSubmit={handleAddBlocked} className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="blockPattern" className="text-xs text-gray-500 mb-1 block">
                Email or @domain.com
              </Label>
              <Input
                id="blockPattern"
                placeholder="spam@example.com or @spam-domain.com"
                value={newPattern}
                onChange={(e) => setNewPattern(e.target.value)}
                disabled={addingBlocked}
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="blockReason" className="text-xs text-gray-500 mb-1 block">
                Reason (optional)
              </Label>
              <Input
                id="blockReason"
                placeholder="Spam, harassment, etc."
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                disabled={addingBlocked}
              />
            </div>
            <Button type="submit" disabled={addingBlocked || !newPattern.trim()} size="sm">
              {addingBlocked ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Block
            </Button>
          </form>

          {/* Blocked list */}
          {blockedEmails.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
              No blocked emails yet. All contact form submissions and emails will be forwarded.
            </p>
          ) : (
            <div className="border dark:border-gray-700 rounded-lg divide-y dark:divide-gray-700">
              {blockedEmails.map((entry, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-red-600 dark:text-red-400">
                      {entry.pattern}
                    </span>
                    {entry.reason && (
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        &mdash; {entry.reason}
                      </span>
                    )}
                    {entry.added_at && (
                      <span className="text-gray-400 dark:text-gray-500 text-xs ml-2">
                        {new Date(entry.added_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 ml-2"
                    onClick={() => handleRemoveBlocked(entry.pattern)}
                    disabled={removingPattern === entry.pattern}
                  >
                    {removingPattern === entry.pattern ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-xs text-gray-500 dark:text-gray-400">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              Blocked senders see a normal "success" message on the contact form &mdash; they won't know they're blocked.
              Blocked emails are also filtered from forwarding.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forwarding Addresses Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-500" />
            Forwarding Addresses
          </CardTitle>
          <CardDescription>
            Emails to info@owazamartialarts.com are forwarded to these addresses.
            All recipients are BCC'd (addresses hidden from each other).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add forwarding email */}
          <div className="flex gap-2">
            <Input
              placeholder="recipient@example.com"
              value={newForwardEmail}
              onChange={(e) => setNewForwardEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddForwardEmail();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddForwardEmail}
              disabled={!newForwardEmail.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Forwarding list */}
          {forwardingList.length === 0 ? (
            <p className="text-sm text-red-500 italic py-2">
              No forwarding addresses configured. Emails will not be forwarded.
            </p>
          ) : (
            <div className="border dark:border-gray-700 rounded-lg divide-y dark:divide-gray-700">
              {forwardingList.map((addr) => (
                <div key={addr} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-mono">{addr}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 ml-2"
                    onClick={() => handleRemoveForwardEmail(addr)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Save button */}
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              Changes take effect immediately after saving.
            </div>
            {forwardingChanged && (
              <Button onClick={handleSaveForwarding} disabled={savingForwarding}>
                {savingForwarding ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Forwarding
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettings;
